import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { ChevronRight, Wrench, User as UserIcon, CheckCircle2, Plus, Trash2, Package, PackageCheck } from "lucide-react";

/**
 * DevicesPanel — Panel resumen de todos los equipos de una orden multi-equipo.
 *
 * Props:
 *  - order: la orden completa (con devices_all y devices_summary desde backend enrich)
 *  - canManage: si el usuario puede eliminar equipos secundarios (admin/supervisor)
 *  - onChanged: callback tras crear/eliminar equipos (refresca padre)
 *  - onDeliverDevice: callback para entrega parcial de un equipo (opcional)
 */
export default function DevicesPanel({ order, canManage = false, onChanged, activeDeviceId = null }) {
  const devices = order?.devices_all || [];
  const summary = order?.devices_summary || { total: 1, completed: 0, pending: 1 };
  if (devices.length <= 1) return null; // Sólo mostramos el panel si hay más de 1 equipo

  const primaryDeviceId = order.id;
  const pending = devices.filter((d) => !isCompleted(d.status));

  async function deleteDevice(did, model) {
    if (!confirm(`¿Eliminar el equipo "${model || ""}" de esta orden? Se borran sus fotos y ítems de presupuesto.`)) return;
    try {
      await api.delete(`/orders/${order.id}/devices/${did}`);
      toast.success("Equipo eliminado");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <section className="rounded-2xl border-2 border-cyan-300/70 bg-gradient-to-br from-cyan-50 to-white p-4 sm:p-5 shadow-sm" data-testid="devices-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package size={20} className="text-cyan-700" />
          <h2 className="font-heading font-extrabold text-slate-900 text-lg sm:text-xl">
            Equipos en esta orden ({summary.total})
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-mono font-bold ${
              summary.pending === 0
                ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                : "bg-amber-100 text-amber-800 border border-amber-300"
            }`}
            data-testid="devices-summary-badge"
          >
            {summary.pending === 0 ? <CheckCircle2 size={13} /> : <PackageCheck size={13} />}
            {summary.completed} de {summary.total} cerrados (entregados o sin reparar)
          </span>
        </div>
      </div>

      {pending.length > 0 && summary.total > 1 && (
        <div className="mt-3 text-xs text-slate-600" data-testid="devices-pending-line">
          <span className="font-semibold text-amber-800">Pendientes:</span>{" "}
          {pending
            .map((p) => `${p.brand || ""} ${p.model || p.subcategory?.name || "Equipo"}`.trim())
            .join(" · ")}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {devices.map((d, idx) => {
          const isPrimary = d.is_primary;
          const targetHref = isPrimary
            ? `/orders/${order.id}`
            : `/orders/${order.id}/devices/${d.id}`;
          // "Activo" = el equipo cuya vista tenemos abierta actualmente
          const effectiveActive = activeDeviceId ? activeDeviceId : order.id;
          const isActive = isPrimary
            ? effectiveActive === order.id
            : effectiveActive === d.id;
          return (
            <div
              key={d.id}
              className={`rounded-xl border p-3 sm:p-4 bg-white hover:border-cyan-500 transition-colors ${
                isActive
                  ? "border-cyan-500 ring-2 ring-cyan-200"
                  : isPrimary
                    ? "border-slate-300"
                    : "border-cyan-200"
              }`}
              data-testid={`device-card-${idx}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                      Equipo #{idx + 1}{isPrimary ? " · Principal" : ""}
                    </span>
                    <StatusBadge status={d.status} size="sm" />
                  </div>
                  <div className="mt-1 font-heading font-bold text-slate-900 text-base break-words leading-snug">
                    {d.subcategory?.name || d.model || d.brand || d.category?.name || "Equipo"}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-600 truncate">
                    {d.brand && <span>{d.brand}</span>}
                    {d.brand && d.model && <span className="text-slate-400"> · </span>}
                    {d.model && <span>{d.model}</span>}
                    {d.serial && (
                      <span className="ml-1 font-mono text-slate-500">· S/N {d.serial}</span>
                    )}
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-600">
                    <UserIcon size={12} />
                    <span className="truncate">
                      {d.technician?.name || <em className="text-slate-400">Sin técnico</em>}
                    </span>
                  </div>
                </div>
                {canManage && !isPrimary && (
                  <button
                    type="button"
                    onClick={() => deleteDevice(d.id, `${d.brand || ""} ${d.model || ""}`.trim())}
                    className="text-slate-400 hover:text-rose-600 p-1"
                    title="Eliminar equipo"
                    data-testid={`device-card-${idx}-delete`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                <span className="text-[11px] font-mono text-slate-500 truncate">
                  {isActive ? "Vista actual" : "Reparar este equipo"}
                </span>
                <Link
                  to={targetHref}
                  className={`inline-flex items-center gap-1 text-xs font-heading font-bold rounded-lg px-3 py-1.5 transition-colors ${
                    isActive
                      ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                      : "bg-cyan-600 text-white hover:bg-cyan-700"
                  }`}
                  data-testid={`device-card-${idx}-open`}
                >
                  <Wrench size={12} /> {isActive ? "Ver" : "Reparar este equipo"} <ChevronRight size={12} />
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {canManage && (
        <div className="mt-3 text-[11px] text-slate-500 italic">
          Puedes agregar más equipos usando el botón "+ Agregar otro equipo" debajo del listado principal.
        </div>
      )}
    </section>
  );
}

function isCompleted(status) {
  return ["DELIVERED", "NOT_REPAIRABLE", "CLIENT_REJECTED"].includes(status);
}
